/**
 * Floating selection actions near the current text selection.
 * Activates a frozen selection snapshot via pointerdown + preventDefault
 * so the browser does not collapse selection before the handler runs.
 */

import { NotebookPen, Quote } from 'lucide-react';

interface QuoteToolbarProps {
  /** Viewport-fixed position (from selection rangeRect). */
  top: number;
  left: number;
  onQuote: () => void;
  /** Optional: attach quote + seed composer with “add as note” (not auto-send). */
  onAddToNote?: () => void;
}

export function QuoteToolbar({
  top,
  left,
  onQuote,
  onAddToNote,
}: QuoteToolbarProps) {
  return (
    <div
      className="fixed z-50 -translate-x-1/2 -translate-y-full"
      style={{ top: `${top}px`, left: `${left}px` }}
      role="toolbar"
      aria-label="Selection actions"
    >
      <div className="mb-1 flex items-center gap-1 rounded-lg border border-stone-200 bg-stone-900 p-0.5 shadow-lg">
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onQuote();
          }}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-white hover:bg-stone-700"
        >
          <Quote className="h-3.5 w-3.5" />
          Quote
        </button>
        {onAddToNote && (
          <button
            type="button"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToNote();
            }}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-white hover:bg-stone-700"
            title="Attach selection as a quote and draft an “add as note” message (not sent until you press Send)"
          >
            <NotebookPen className="h-3.5 w-3.5" />
            Add to note
          </button>
        )}
      </div>
    </div>
  );
}
